'use client';

interface ActivityEntry {
  id: string;
  action: string;
  object_type: string;
  object_name: string | null;
  created_at: string;
}

interface RecentActivityWidgetProps {
  activities: ActivityEntry[];
  loading?: boolean;
}

const ACTION_ICONS: Record<string, string> = {
  entity_created: '➕',
  entity_updated: '✏️',
  entity_deleted: '🗑️',
  link_created: '🔗',
  link_deleted: '💔',
  mapping_created: '🗺️',
  mapping_synced: '🔄',
  assessment_created: '📝',
  assessment_completed: '✅',
  assessment_updated: '📊',
  assessment_deleted: '🗑️',
  match_accepted: '✓',
  match_rejected: '✗',
};

const ACTION_LABELS: Record<string, string> = {
  entity_created: 'Created entity',
  entity_updated: 'Updated entity',
  entity_deleted: 'Deleted entity',
  link_created: 'Created link',
  link_deleted: 'Deleted link',
  mapping_created: 'Mapped to ArchiMate',
  mapping_synced: 'Synced mapping',
  assessment_created: 'Started assessment',
  assessment_completed: 'Completed assessment',
  assessment_updated: 'Updated assessment',
  assessment_deleted: 'Deleted assessment',
  match_accepted: 'Accepted match',
  match_rejected: 'Rejected match',
};

export function RecentActivityWidget({ activities, loading }: RecentActivityWidgetProps) {
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-lg font-semibold text-light-text dark:text-foreground mb-4">Recent Activity</h3>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 bg-light-border dark:bg-background rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-light-border dark:bg-background rounded w-3/4 mb-1" />
                <div className="h-3 bg-light-border dark:bg-background rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
      <h3 className="text-lg font-semibold text-light-text dark:text-foreground mb-4">Recent Activity</h3>

      {activities.length === 0 ? (
        <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
          <div className="text-4xl mb-2">📭</div>
          <p className="text-sm">No recent activity</p>
          <p className="text-xs mt-1">Actions will appear here as you work</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 group">
              <div className="w-8 h-8 rounded-full bg-light-border dark:bg-background flex items-center justify-center text-sm">
                {ACTION_ICONS[activity.action] || '📋'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-light-text dark:text-foreground">
                  <span className="font-medium">{ACTION_LABELS[activity.action] || activity.action}</span>
                  {activity.object_name && (
                    <span className="text-light-text-secondary dark:text-foreground-secondary">
                      {' '}&quot;{activity.object_name}&quot;
                    </span>
                  )}
                </p>
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                  {formatTime(activity.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
